import * as React from 'react';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { ActionButton } from 'components/action-button';
import { GeolocationDeniedModal } from 'components/geolocation-permission-modal/geolocation-denied-modal';
import { EmptyStateActions as ActionOptions } from 'components/store-locator-empty-states/types';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames } from 'state/cdp/constants';
import { useGeolocation } from 'state/geolocation';
import { routes } from 'utils/routing';

import { Wrapper } from './empty-state-action.styled';

/**
 *
 * EmptyStateAction enables displaying action options, i.e. a Sign In button, for different empty states
 *
 */
export const EmptyStateAction: FC<{ action: ActionOptions }> = ({ action }) => {
  const { isPermissionPrompt, isPermissionDenied, setPermissionChecking, loadCurrentPosition } =
    useGeolocation();
  const { formatMessage } = useIntl();
  const cdp = useCdpContext();

  const [geolocationDeniedModalOpen, setGeolocationDeniedModalOpen] = useState(false);

  const onSignInClick = useCallback(() => {
    cdp.logNavigationClick(CustomEventNames.BUTTON_CLICK_SIGN_IN);
  }, [cdp]);

  const handleGeolocationPrompt = useCallback(() => {
    if (isPermissionDenied) {
      setGeolocationDeniedModalOpen(true);
    } else if (isPermissionPrompt) {
      setPermissionChecking();
      loadCurrentPosition();
    }
  }, [isPermissionDenied, isPermissionPrompt, loadCurrentPosition, setPermissionChecking]);

  const dismissModal = () => setGeolocationDeniedModalOpen(false);

  const ActionComponent: ReactNode | null = useMemo(() => {
    switch (action) {
      case ActionOptions.LOGIN:
        return (
          <ActionButton to={routes.signIn} onClick={onSignInClick} data-testid="signin-button">
            {formatMessage({ id: 'signIn' })}
          </ActionButton>
        );
      case ActionOptions.SHARE_LOCATION:
        return (
          <>
            <GeolocationDeniedModal
              isOpen={geolocationDeniedModalOpen}
              dismissModal={dismissModal}
            />
            <ActionButton data-testid="share-location-button" onClick={handleGeolocationPrompt}>
              {formatMessage({ id: 'shareMyLocation' })}
            </ActionButton>
          </>
        );
      default:
        return null;
    }
  }, [action, formatMessage, geolocationDeniedModalOpen, handleGeolocationPrompt, onSignInClick]);

  if (!ActionComponent) {
    return null;
  }

  return <Wrapper data-testid="empty-state-action-wrapper">{ActionComponent}</Wrapper>;
};
